import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, EmptyState, Field, Icon, Input, Modal, Select, Spinner, StatusPill, Textarea, useToast } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { brl } from '../../lib/format';
import { usePageMeta } from '../../lib/seo';
import { tr } from '../../lib/i18n';

interface ServiceRow {
  id: string;
  name: string;
  description: string | null;
  category_id: string | null;
  categoryLabel: string | null;
  price_cents: number;
  duration_minutes: number;
  buffer_minutes: number;
  status: 'active' | 'inactive';
  online_booking: number;
  rules: string | null;
  professionalCount: number;
  upcomingCount: number;
}

interface CategoryRow {
  id: string;
  key: string;
  label: string;
}

interface ServiceRules {
  minAdvanceMinutes?: number;
  maxFutureDays?: number;
  requiresApproval?: boolean;
}

interface FormState {
  name: string;
  description: string;
  categoryKey: string;
  priceBRL: string;
  durationMinutes: string;
  bufferMinutes: string;
  onlineBooking: boolean;
  minAdvanceMinutes: string;
  maxFutureDays: string;
  requiresApproval: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  categoryKey: '',
  priceBRL: '',
  durationMinutes: '60',
  bufferMinutes: '0',
  onlineBooking: true,
  minAdvanceMinutes: '',
  maxFutureDays: '',
  requiresApproval: false
};

export function PanelServicesPage(): JSX.Element {
  usePageMeta(tr('Serviços'), tr('Cadastre, edite, ative, desative e exclua os serviços do estabelecimento.'));
  const { active } = useAuth();
  const toast = useToast();
  const businessId = active?.businessId ?? '';
  const canManage = active ? active.role === 'owner' || active.role === 'manager' : false;

  const [services, setServices] = useState<ServiceRow[]>([]);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [modal, setModal] = useState<{ mode: 'create' } | { mode: 'edit'; service: ServiceRow } | null>(null);

  const load = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ services: ServiceRow[]; categories: CategoryRow[] }>(`/api/services/${businessId}/services`);
      setServices(res.services);
      setCategories(res.categories);
    } catch (err) {
      setError(err);
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleStatus = async (s: ServiceRow): Promise<void> => {
    const next = s.status === 'active' ? 'inactive' : 'active';
    setBusy(`toggle:${s.id}`);
    try {
      await api.patch<{ ok: boolean }>(`/api/services/${businessId}/services/${s.id}`, { status: next });
      toast(tr('Serviço "{name}" agora está {status}.', { name: s.name, status: next === 'active' ? tr('ativo') : tr('inativo') }), 'success');
      await load();
    } catch (err) {
      toast(errorMessage(err), 'danger');
    } finally {
      setBusy(null);
    }
  };

  const remove = async (s: ServiceRow): Promise<void> => {
    setBusy(`delete:${s.id}`);
    try {
      const res = await api.del<{ ok: boolean; deactivated: boolean; message: string }>(`/api/services/${businessId}/services/${s.id}`);
      setNotice(res.message);
      toast(res.message, res.deactivated ? 'info' : 'success');
      await load();
    } catch (err) {
      toast(errorMessage(err), 'danger');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{tr("Serviços")}</h1>
          <p className="nl-hint">{tr("Preço, duração, intervalo adicional, disponibilidade online e regras de agendamento.")}</p>
        </div>
        {canManage ? (
          <Button onClick={() => setModal({ mode: 'create' })} icon={<Icon name="plus" size={16} />}>
            {tr("Novo serviço")}
          </Button>
        ) : null}
      </div>

      {!canManage ? (
        <Alert tone="info" title={tr("Somente leitura")}>
          {tr("Seu perfil de profissional visualiza os serviços, mas apenas proprietário ou gerente pode criar, editar, ativar/desativar ou excluir.")}
        </Alert>
      ) : null}

      {notice ? (
        <Alert tone="warning" title={tr("Resultado da exclusão")}>
          {notice}
        </Alert>
      ) : null}

      {loading ? (
        <Card>
          <Spinner label={tr("Carregando serviços…")} />
        </Card>
      ) : null}

      {!loading && error ? <ServicesError error={error} /> : null}

      {!loading && !error ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {services.length === 0 ? (
            <div className="lg:col-span-2">
              <EmptyState
                title={tr("Nenhum serviço cadastrado")}
                description={canManage ? tr('Crie o primeiro serviço para começar a receber agendamentos.') : tr('Peça ao proprietário ou gerente para cadastrar os serviços.')}
                action={canManage ? <Button onClick={() => setModal({ mode: 'create' })}>{tr("Criar serviço")}</Button> : undefined}
              />
            </div>
          ) : (
            services.map((s) => {
              const rules = parseRules(s.rules);
              return (
                <Card key={s.id} as="article" className="min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="text-lg font-semibold">{s.name}</h2>
                      <p className="nl-hint">
                        {s.categoryLabel ?? 'Sem categoria'} · {brl(s.price_cents)} · {s.duration_minutes} {tr("min")}
                        {s.buffer_minutes > 0 ? ` + ${s.buffer_minutes} min de intervalo` : ''}
                      </p>
                    </div>
                    <StatusPill tone={s.status === 'active' ? 'positive' : 'neutral'}>{s.status === 'active' ? tr('Ativo') : tr('Inativo')}</StatusPill>
                  </div>
                  {s.description ? <p className="mt-2 text-sm text-muted">{s.description}</p> : null}
                  <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <Info label={tr("Agendamento online")} value={s.online_booking === 1 ? tr('Permitido') : tr('Bloqueado')} />
                    <Info label={tr("Profissionais")} value={`${s.professionalCount}`} />
                    <Info label={tr("Agendamentos futuros")} value={`${s.upcomingCount}`} />
                    <Info label={tr("Antecedência mínima")} value={rules.minAdvanceMinutes !== undefined ? `${rules.minAdvanceMinutes} min` : 'Padrão do sistema'} />
                    <Info label={tr("Horizonte")} value={rules.maxFutureDays !== undefined ? `${rules.maxFutureDays} dias` : 'Padrão do sistema'} />
                    <Info label={tr("Aprovação")} value={rules.requiresApproval ? tr('Exige aprovação') : tr('Confirma automática')} />
                  </dl>
                  {canManage ? (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button variant="secondary" disabled={busy !== null} onClick={() => setModal({ mode: 'edit', service: s })}>
                        {tr("Editar")}
                      </Button>
                      <Button variant="ghost" loading={busy === `toggle:${s.id}`} disabled={busy !== null} onClick={() => void toggleStatus(s)}>
                        {s.status === 'active' ? tr('Desativar') : tr('Ativar')}
                      </Button>
                      <Button variant="danger" loading={busy === `delete:${s.id}`} disabled={busy !== null} onClick={() => void remove(s)}>
                        {tr("Excluir")}
                      </Button>
                    </div>
                  ) : null}
                </Card>
              );
            })
          )}
        </div>
      ) : null}

      {modal ? (
        <ServiceFormModal
          key={modal.mode === 'edit' ? modal.service.id : 'create'}
          businessId={businessId}
          categories={categories}
          initial={modal.mode === 'edit' ? toForm(modal.service, categories) : EMPTY_FORM}
          editId={modal.mode === 'edit' ? modal.service.id : null}
          onClose={() => setModal(null)}
          onSaved={async (message) => {
            setModal(null);
            toast(message, 'success');
            await load();
          }}
        />
      ) : null}
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt className="nl-hint text-xs">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

function parseRules(raw: string | null): ServiceRules {
  if (!raw) return {};
  try {
    const v = JSON.parse(raw) as unknown;
    return v && typeof v === 'object' ? (v as ServiceRules) : {};
  } catch {
    return {};
  }
}

function toForm(s: ServiceRow, categories: CategoryRow[]): FormState {
  const rules = parseRules(s.rules);
  const categoryKey = categories.find((c) => c.id === s.category_id)?.key ?? '';
  return {
    name: s.name,
    description: s.description ?? '',
    categoryKey,
    priceBRL: (s.price_cents / 100).toFixed(2).replace('.', ','),
    durationMinutes: String(s.duration_minutes),
    bufferMinutes: String(s.buffer_minutes),
    onlineBooking: s.online_booking === 1,
    minAdvanceMinutes: rules.minAdvanceMinutes !== undefined ? String(rules.minAdvanceMinutes) : '',
    maxFutureDays: rules.maxFutureDays !== undefined ? String(rules.maxFutureDays) : '',
    requiresApproval: Boolean(rules.requiresApproval)
  };
}

function parsePriceToCents(raw: string): number | null {
  const t = raw.trim().replace(/R\$|\s/g, '');
  if (!t) return null;
  const normalized = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t;
  const v = Number(normalized);
  if (!Number.isFinite(v) || v < 0) return null;
  return Math.round(v * 100);
}

function ServiceFormModal({
  businessId,
  categories,
  initial,
  editId,
  onClose,
  onSaved
}: {
  businessId: string;
  categories: CategoryRow[];
  initial: FormState;
  editId: string | null;
  onClose: () => void;
  onSaved: (message: string) => Promise<void>;
}): JSX.Element {
  const isEdit = editId !== null;
  const [form, setForm] = useState<FormState>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const set = (patch: Partial<FormState>): void => setForm((f) => ({ ...f, ...patch }));

  const priceCents = parsePriceToCents(form.priceBRL);
  const duration = Number(form.durationMinutes);
  const buffer = Number(form.bufferMinutes);
  const formError =
    form.name.trim().length < 2
      ? tr('O nome deve ter ao menos 2 caracteres.')
      : priceCents === null
        ? tr('Preço inválido. Use o formato 85,00 (reais).')
        : !Number.isInteger(duration) || duration < 5 || duration > 1440
          ? tr('A duração deve ser um número inteiro entre 5 e 1440 minutos.')
          : !Number.isInteger(buffer) || buffer < 0 || buffer > 240
            ? tr('O intervalo adicional deve ser um número inteiro entre 0 e 240 minutos.')
            : null;

  const submit = async (): Promise<void> => {
    if (priceCents === null || formError) return;
    setSaving(true);
    setError(null);
    const rules: ServiceRules = {};
    if (form.minAdvanceMinutes !== '') rules.minAdvanceMinutes = Number(form.minAdvanceMinutes);
    if (form.maxFutureDays !== '') rules.maxFutureDays = Number(form.maxFutureDays);
    rules.requiresApproval = form.requiresApproval;
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      categoryKey: form.categoryKey,
      priceCents,
      durationMinutes: duration,
      bufferMinutes: buffer,
      onlineBooking: form.onlineBooking,
      rules
    };
    try {
      if (editId) {
        await api.patch(`/api/services/${businessId}/services/${editId}`, payload);
        await onSaved(tr('Serviço atualizado com sucesso.'));
      } else {
        await api.post(`/api/services/${businessId}/services`, payload);
        await onSaved(tr('Serviço criado com sucesso.'));
      }
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={isEdit ? tr('Editar serviço') : tr('Novo serviço')}>
      <div className="space-y-3">
        <Field label={tr("Nome")} required>
          {({ id }) => <Input id={id} value={form.name} onChange={(e) => set({ name: e.target.value })} maxLength={120} minLength={2} />}
        </Field>
        <Field label={tr("Descrição")}>
          {({ id }) => <Textarea id={id} rows={2} maxLength={1000} value={form.description} onChange={(e) => set({ description: e.target.value })} />}
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={tr("Categoria")} hint="Lista de categorias ativas da plataforma.">
            {({ id }) => (
              <Select id={id} value={form.categoryKey} onChange={(e) => set({ categoryKey: e.target.value })}>
                <option value="">{tr("Sem categoria")}</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={tr("Preço (R$)")} required hint="Em reais — enviado ao backend em centavos.">
            {({ id }) => <Input id={id} inputMode="decimal" placeholder="85,00" value={form.priceBRL} onChange={(e) => set({ priceBRL: e.target.value })} />}
          </Field>
          <Field label={tr("Duração (min)")} required>
            {({ id }) => <Input id={id} type="number" min={5} max={1440} step={1} value={form.durationMinutes} onChange={(e) => set({ durationMinutes: e.target.value })} />}
          </Field>
          <Field label={tr("Intervalo adicional (min)")} hint="Tempo de limpeza entre atendimentos (0–240).">
            {({ id }) => <Input id={id} type="number" min={0} max={240} step={1} value={form.bufferMinutes} onChange={(e) => set({ bufferMinutes: e.target.value })} />}
          </Field>
        </div>
        <fieldset className="rounded-2xl border border-line p-3">
          <legend className="px-1 text-sm font-semibold">{tr("Regras de agendamento")}</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={tr("Antecedência mínima (min)")} hint="Vazio usa o padrão do sistema.">
              {({ id }) => <Input id={id} type="number" min={0} max={10080} step={1} value={form.minAdvanceMinutes} onChange={(e) => set({ minAdvanceMinutes: e.target.value })} />}
            </Field>
            <Field label={tr("Horizonte máximo (dias)")} hint="Quão longe no futuro se pode agendar (1–365).">
              {({ id }) => <Input id={id} type="number" min={1} max={365} step={1} value={form.maxFutureDays} onChange={(e) => set({ maxFutureDays: e.target.value })} />}
            </Field>
          </div>
          <label className="mt-2 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.requiresApproval} onChange={(e) => set({ requiresApproval: e.target.checked })} />
            {tr("Exige aprovação do estabelecimento")}
          </label>
          <label className="mt-2 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.onlineBooking} onChange={(e) => set({ onlineBooking: e.target.checked })} />
            {tr("Disponível para agendamento online")}
          </label>
        </fieldset>
        {formError ? <p className="text-sm text-danger">{formError}</p> : null}
        {error ? <ServicesError error={error} /> : null}
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          {tr("Fechar")}
        </Button>
        <Button onClick={() => void submit()} loading={saving} disabled={saving || formError !== null}>
          {isEdit ? tr('Salvar alterações') : tr('Criar serviço')}
        </Button>
      </div>
    </Modal>
  );
}

function ServicesError({ error }: { error: unknown }): JSX.Element {
  if (error instanceof ApiError && error.status === 402) {
    return (
      <Alert tone="danger" title={tr("Assinatura necessária")}>
        {error.message}{' '}
        <Link className="font-semibold underline" to="/painel/plano">
          {tr("Ir para Meu plano")}
        </Link>
        .
      </Alert>
    );
  }
  if (error instanceof ApiError && error.status === 403) {
    return (
      <Alert tone="warning" title={tr("Permissão insuficiente")}>
        {tr("Seu perfil não pode gerenciar serviços.")} {error.message}
      </Alert>
    );
  }
  return <Alert tone="danger" title={tr("Erro")}>{error instanceof Error ? error.message : 'Não foi possível concluir a operação.'}</Alert>;
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError && err.status === 402) return tr('{message} Contrate um plano em /painel/plano.', { message: err.message });
  if (err instanceof ApiError && err.status === 403) return tr('Permissão insuficiente. {message}', { message: err.message });
  return err instanceof Error ? err.message : 'Não foi possível concluir a operação.';
}
